import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { Callout, Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

/**
 * Landing page for a scanned machine label: /m/:token
 *
 * why a redirect rather than rendering here: the machine screen is the same
 * whether you arrived by scan, search or link. This page's only jobs are to
 * require sign-in first (directive §10) and to translate the opaque QR token
 * into a machine id.
 */
export function QrLandingPage() {
  const { token = '' } = useParams();
  const { status, restore } = useAuth();

  useEffect(() => {
    if (status === 'loading') void restore();
  }, [status, restore]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['machine', 'by-qr', token],
    queryFn: () => api.get<{ id: string; code: string }>(`/api/machines/by-qr/${token}`),
    enabled: status === 'authenticated' && token.length > 0,
    retry: false,
  });

  if (status === 'loading') return <Spinner label="Checking your sign-in" />;

  if (status === 'anonymous') {
    // Keep the destination so we can come back after sign-in.
    sessionStorage.setItem('odr.afterLogin', `/m/${token}`);
    return <Navigate to="/login" replace />;
  }

  if (isLoading) return <Spinner label="Reading machine label" />;

  if (error || !data) {
    return (
      <div className="grid min-h-dvh place-items-center bg-steel-100 p-4">
        <div className="w-full max-w-sm">
          <Callout tone="alarm" title="Label not recognised">
            This QR label is not registered in ODR, or it has been reissued. Ask your supervisor
            to check the machine label, or find the machine by its ID instead.
          </Callout>
          <a
            href="/scan"
            className="mt-4 block rounded-lg bg-steel-800 px-4 py-3 text-center text-sm font-semibold text-white"
          >
            Back to scanning
          </a>
        </div>
      </div>
    );
  }

  return <Navigate to={`/machines/${data.id}`} replace />;
}
